<template>
  <div class="membresia-editor">
    <!-- Encabezado -->
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-2"
              @click="$emit('close')"
              :disabled="saving"
            >
              <i class="fa fa-arrow-left mr-2"></i>
              Volver al listado
            </button>
            <h5 class="mb-1 font-weight-bold">
              <i class="fa fa-edit mr-2 text-primary"></i>
              Editar Membresía
              <span class="badge ml-2" :class="tipo === 'principal' ? 'badge-success' : 'badge-info'">
                {{ tipo === 'principal' ? 'Principal' : 'Secundaria' }}
              </span>
            </h5>
            <p class="text-muted mb-0">
              Socio: <strong>{{ socio.nombreSocio }}</strong> · RUT <strong>{{ socio.rut }}</strong>
            </p>
            <p class="text-muted small mb-0" v-if="form.descripcionPlan">
              Plan actual: <strong>{{ form.descripcionPlan }}</strong>
            </p>
            <p class="text-muted small mb-0" v-if="tipo === 'adicional' && membresia._id">
              <i class="fa fa-fingerprint mr-1"></i>
              ID Membresía: <code>{{ membresia._id }}</code>
            </p>
          </div>
          <div class="text-right mt-2 mt-md-0">
            <button
              type="button"
              class="btn btn-outline-secondary mr-2"
              @click="$emit('close')"
              :disabled="saving"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="guardarCambios"
              :disabled="saving"
            >
              <span v-if="saving">
                <i class="fa fa-spinner fa-spin mr-2"></i>
                Guardando...
              </span>
              <span v-else>
                <i class="fa fa-save mr-2"></i>
                Guardar cambios
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <form @submit.prevent="guardarCambios">
      <!-- Datos del plan -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-cube text-primary mr-2"></i>
            Datos del Plan
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row">
            <div class="form-group col-md-6">
              <label class="small text-muted mb-1">Familia</label>
              <select class="form-control" v-model="form.idFamilia" @change="onFamiliaChange">
                <option v-if="!familiasActivas.length" :value="form.idFamilia">
                  {{ form.nombreFamilia || '—' }}
                </option>
                <option
                  v-for="f in familiasActivas"
                  :key="f._id || f.id"
                  :value="f.id"
                >
                  {{ f.nombre }}
                </option>
              </select>
              <small class="form-text text-muted" v-if="!familiasActivas.length">
                No se pudo cargar el catálogo de familias. Manteniendo valor actual.
              </small>
            </div>
            <div class="form-group col-md-6">
              <label class="small text-muted mb-1">SubFamilia</label>
              <select class="form-control" v-model="form.idSubFamilia" @change="onSubFamiliaChange">
                <option v-if="!subfamiliasFiltradas.length" :value="form.idSubFamilia">
                  {{ form.nombreSubFamilia || '—' }}
                </option>
                <option
                  v-for="sf in subfamiliasFiltradas"
                  :key="sf._id || sf.id"
                  :value="sf.id"
                >
                  {{ sf.nombre }}
                </option>
              </select>
              <small class="form-text text-muted" v-if="!subfamiliasFiltradas.length && form.idFamilia">
                No hay subfamilias asociadas a la familia seleccionada.
              </small>
            </div>
          </div>
          <div class="form-group">
            <label class="small text-muted mb-1">Descripción del Plan</label>
            <input
              type="text"
              class="form-control"
              v-model="form.descripcionPlan"
              maxlength="200"
              placeholder="Ej: MENSUAL PROMOCION"
            />
          </div>
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Sucursal de Asignación</label>
              <input type="text" class="form-control" :value="nombreSucursalAsignacion" disabled />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">ID Familia</label>
              <input type="number" class="form-control" :value="form.idFamilia" disabled />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">ID Plan</label>
              <input type="number" class="form-control" :value="form.idPlan" disabled />
            </div>
          </div>
        </div>
      </section>

      <!-- Configuración económica -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-money text-primary mr-2"></i>
            Configuración Económica
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Valor del Plan (CLP)</label>
              <input type="number" class="form-control" v-model.number="form.valorPlan" min="0" />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Tipo de IVA</label>
              <select class="form-control" v-model="form.tipoIVA">
                <option value="EXENTO">EXENTO</option>
                <option value="AFECTO">AFECTO</option>
              </select>
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Convenio</label>
              <select class="form-control" v-model="form.convenio">
                <option v-if="!convenios.length" :value="form.convenio">{{ form.convenio || '—' }}</option>
                <option v-for="opt in convenios" :key="opt._id || opt.glosa" :value="opt.glosa">
                  {{ opt.glosa }}
                </option>
              </select>
            </div>
          </div>
          <div class="row align-items-end">
            <div class="form-group col-md-3">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.indicadorMatricula" />
                <span>Cobra matrícula</span>
              </label>
            </div>
            <div class="form-group col-md-3">
              <label class="small text-muted mb-1">Valor Matrícula (CLP)</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.valorMatricula"
                min="0"
                :disabled="!form.indicadorMatricula"
              />
            </div>
            <div class="form-group col-md-3">
              <label class="small text-muted mb-1">Fecha Pago Matrícula</label>
              <input
                type="text"
                class="form-control"
                v-model="form.fechaPagoMatricula"
                placeholder="DD-MM-AAAA"
                :disabled="!form.indicadorMatricula"
              />
            </div>
            <div class="form-group col-md-3">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.indicadorFactura" />
                <span>Emite factura</span>
              </label>
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Porcentaje de Descuento (%)</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.porcentajeDescuento"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Días Permitidos para Congelar</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.diasPermitidoCongelar"
                min="0"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Duración y mes calendario -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-calendar text-primary mr-2"></i>
            Duración y Mes Calendario
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Duración del Plan</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.duracionPlan"
                min="0"
              />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Unidad de Duración</label>
              <select class="form-control" v-model="form.unidadDuracionPlan">
                <option v-if="!unidadesDuracion.length" :value="form.unidadDuracionPlan">
                  {{ form.unidadDuracionPlan || '—' }}
                </option>
                <option v-for="opt in unidadesDuracion" :key="opt._id || opt.glosa" :value="opt.glosa">
                  {{ opt.glosa }}
                </option>
              </select>
            </div>
            <div class="form-group col-md-4">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.indicadorMesCalendario" />
                <span>Mes calendario</span>
              </label>
            </div>
          </div>
          <div class="row" v-if="!form.indicadorMesCalendario">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Días Mes No Calendario</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.diasMesNoCalendario"
                min="0"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Sesiones -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-list-ol text-primary mr-2"></i>
            Sesiones
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row align-items-end">
            <div class="form-group col-md-4">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.indicadorUsoSesiones" />
                <span>Usa sesiones</span>
              </label>
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Sesiones Límite</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.sesionesLimite"
                min="0"
                :disabled="!form.indicadorUsoSesiones"
              />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Sesiones Utilizadas</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.sesionesUtilizadas"
                min="0"
                :disabled="!form.indicadorUsoSesiones"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Renovación -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-refresh text-primary mr-2"></i>
            Renovación
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row align-items-end">
            <div class="form-group col-md-4">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.indicadorPermiteRenovar" />
                <span>Permite renovar</span>
              </label>
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Expiración Renovación</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.expiracionRenovacion"
                min="0"
                :disabled="!form.indicadorPermiteRenovar"
              />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Unidad Expiración Renovación</label>
              <select
                class="form-control"
                v-model="form.unidadExpiracionRenovacion"
                :disabled="!form.indicadorPermiteRenovar"
              >
                <option v-if="!unidadesExpiracionRenovacion.length" :value="form.unidadExpiracionRenovacion">
                  {{ form.unidadExpiracionRenovacion || '—' }}
                </option>
                <option v-for="opt in unidadesExpiracionRenovacion" :key="opt._id || opt.glosa" :value="opt.glosa">
                  {{ opt.glosa }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <!-- Estado y fechas -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-clock-o text-primary mr-2"></i>
            Estado y Fechas
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Estado del Plan</label>
              <select class="form-control" v-model="form.estadoPlan">
                <option v-if="!estadosPago.length" :value="form.estadoPlan">{{ form.estadoPlan || '—' }}</option>
                <option v-for="opt in estadosPago" :key="opt._id || opt.glosa" :value="opt.glosa">
                  {{ opt.glosa }}
                </option>
              </select>
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Fecha Asignación</label>
              <input type="text" class="form-control" v-model="form.fechaAsignacion" placeholder="DD-MM-AAAA" />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Fecha Inicio</label>
              <input type="text" class="form-control" v-model="form.fechaInicio" placeholder="DD-MM-AAAA" />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Fecha Vencimiento</label>
              <input type="text" class="form-control" v-model="form.fechaVencimiento" placeholder="DD-MM-AAAA" />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Fecha Vigencia</label>
              <input type="text" class="form-control" v-model="form.fechaVigencia" placeholder="DD-MM-AAAA" />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Fecha Último Pago</label>
              <input type="text" class="form-control" v-model="form.fechaUltimoPago" placeholder="DD-MM-AAAA" />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">Hora Último Pago</label>
              <input type="text" class="form-control" v-model="form.horaUltimoPago" placeholder="HH:MM:SS" />
            </div>
          </div>
        </div>
      </section>

      <!-- Información Adicional -->
      <section class="card shadow-sm border-0 mb-4">
        <div class="card-header bg-white border-0">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-info-circle text-primary mr-2"></i>
            Información Adicional
          </h6>
        </div>
        <div class="card-body pt-0">
          <div class="row">
            <div class="form-group col-md-3">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.informacionAdicional.multiSede" />
                <span>Multi sede</span>
              </label>
            </div>
            <div class="form-group col-md-3">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.informacionAdicional.descuentoSesionesXDia" />
                <span>Descontar sesiones por día</span>
              </label>
            </div>
            <div class="form-group col-md-3">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.informacionAdicional.esSuscripcion" />
                <span>Es suscripción</span>
              </label>
            </div>
            <div class="form-group col-md-3">
              <label class="custom-toggle mb-1">
                <input type="checkbox" v-model="form.informacionAdicional.limitarPuntoAcceso" />
                <span>Limitar punto de acceso</span>
              </label>
            </div>
          </div>

          <!-- Beneficios adicionales (lista) -->
          <div class="mt-3">
            <label class="small text-muted mb-1">
              <strong>Beneficios Adicionales</strong>
            </label>
            <div
              v-for="(_, idx) in form.informacionAdicional.beneficiosAdicionales"
              :key="`benef-${idx}`"
              class="d-flex align-items-center mb-2"
            >
              <input
                type="text"
                class="form-control mr-2"
                v-model="form.informacionAdicional.beneficiosAdicionales[idx]"
                placeholder="Ej: Personal Trainer"
              />
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                @click="quitarBeneficio(idx)"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="agregarBeneficio">
              <i class="fa fa-plus mr-1"></i>
              Agregar beneficio
            </button>
          </div>

          <!-- Puntos de acceso permitidos -->
          <div class="mt-4" v-if="form.informacionAdicional.limitarPuntoAcceso">
            <label class="small text-muted mb-1">
              <strong>Puntos de Acceso Permitidos</strong>
            </label>
            <div
              v-for="(punto, idx) in form.informacionAdicional.puntoAccesoPermitidos"
              :key="`punto-${idx}`"
              class="row align-items-center mb-2"
            >
              <div class="col-md-7">
                <input
                  type="text"
                  class="form-control"
                  v-model="punto.idAcceso"
                  placeholder="ID de acceso"
                />
              </div>
              <div class="col-md-3">
                <label class="custom-toggle mb-0">
                  <input type="checkbox" v-model="punto.descontarSesion" />
                  <span>Descontar sesión</span>
                </label>
              </div>
              <div class="col-md-2 text-right">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="quitarPuntoAcceso(idx)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="agregarPuntoAcceso">
              <i class="fa fa-plus mr-1"></i>
              Agregar punto de acceso
            </button>
          </div>
        </div>
      </section>

      <!-- Turno (editable) -->
      <section class="card shadow-sm border-0 mb-4" v-if="form.turno">
        <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center flex-wrap">
          <h6 class="mb-0 font-weight-bold">
            <i class="fa fa-calendar-check-o text-primary mr-2"></i>
            Turno asignado
          </h6>
          <span v-if="turnoModificado" class="badge badge-warning">
            <i class="fa fa-pencil mr-1"></i>
            Turno modificado · se enviará al guardar
          </span>
        </div>
        <div class="card-body pt-0">
          <div class="row">
            <div class="form-group col-md-8">
              <label class="small text-muted mb-1">Descripción del Turno</label>
              <input
                type="text"
                class="form-control"
                v-model="form.turno.descripcionTurno"
                placeholder="Ej: SEMANA LIBRE"
              />
            </div>
            <div class="form-group col-md-4">
              <label class="small text-muted mb-1">ID Turno</label>
              <input type="number" class="form-control" :value="form.turno.idTurno" disabled />
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Día</th>
                  <th class="text-center" style="width: 130px;">Autorizado</th>
                  <th class="text-center" style="width: 180px;">Hora Inicio</th>
                  <th class="text-center" style="width: 180px;">Hora Término</th>
                  <th>Validación</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dia in dias" :key="dia.key">
                  <td><strong>{{ dia.label }}</strong></td>
                  <td class="text-center">
                    <label class="custom-toggle mb-0 justify-content-center">
                      <input
                        type="checkbox"
                        v-model="form.turno[`${dia.key}Autorizado`]"
                      />
                      <span class="small">{{ form.turno[`${dia.key}Autorizado`] ? 'Sí' : 'No' }}</span>
                    </label>
                  </td>
                  <td>
                    <input
                      type="time"
                      step="1"
                      class="form-control form-control-sm"
                      v-model="form.turno[`${dia.key}HoraInicio`]"
                      :disabled="!form.turno[`${dia.key}Autorizado`]"
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      step="1"
                      class="form-control form-control-sm"
                      v-model="form.turno[`${dia.key}HoraTermino`]"
                      :disabled="!form.turno[`${dia.key}Autorizado`]"
                    />
                  </td>
                  <td>
                    <small v-if="erroresTurno[dia.key]" class="text-danger">
                      <i class="fa fa-exclamation-triangle mr-1"></i>
                      {{ erroresTurno[dia.key] }}
                    </small>
                    <small v-else-if="form.turno[`${dia.key}Autorizado`]" class="text-success">
                      <i class="fa fa-check mr-1"></i>
                      OK
                    </small>
                    <small v-else class="text-muted">—</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mt-2" v-if="turnoTieneErrores">
            <div class="alert alert-warning mb-0 py-2 px-3 small">
              <i class="fa fa-exclamation-circle mr-1"></i>
              Hay días con horario inválido. La hora de término debe ser posterior a la hora de inicio.
            </div>
          </div>
        </div>
      </section>

      <!-- Acciones inferiores -->
      <div class="d-flex justify-content-end mb-5">
        <button
          type="button"
          class="btn btn-outline-secondary mr-2"
          @click="$emit('close')"
          :disabled="saving"
        >
          Cancelar
        </button>
        <button type="submit" class="btn btn-success" :disabled="saving">
          <span v-if="saving">
            <i class="fa fa-spinner fa-spin mr-2"></i>
            Guardando...
          </span>
          <span v-else>
            <i class="fa fa-save mr-2"></i>
            Guardar cambios
          </span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
const DIAS = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' }
];

function glosaOrString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.glosa) return value.glosa;
  return '';
}

export default {
  name: 'MembresiaEditor',
  props: {
    membresia: { type: Object, required: true },
    socio: { type: Object, required: true },
    tipo: { type: String, required: true, validator: v => ['principal', 'adicional'].includes(v) },
    catalogos: {
      type: Object,
      default: () => ({
        unidadesDuracion: [],
        convenios: [],
        unidadesExpiracionRenovacion: [],
        estadosPago: [],
        familias: [],
        subfamilias: []
      })
    }
  },
  data() {
    return {
      saving: false,
      dias: DIAS,
      form: this.buildForm(this.membresia),
      turnoOriginal: this.membresia.turno
        ? JSON.parse(JSON.stringify(this.membresia.turno))
        : null
    };
  },
  computed: {
    unidadesDuracion() { return this.catalogos.unidadesDuracion || []; },
    convenios() { return this.catalogos.convenios || []; },
    unidadesExpiracionRenovacion() { return this.catalogos.unidadesExpiracionRenovacion || []; },
    estadosPago() { return this.catalogos.estadosPago || []; },
    familiasActivas() {
      return (this.catalogos.familias || []).filter(f => f.activo !== false);
    },
    subfamiliasFiltradas() {
      const lista = (this.catalogos.subfamilias || []).filter(sf => sf.activo !== false);
      const idFamilia = this.form.idFamilia;
      if (!idFamilia) return lista;
      return lista.filter(sf => {
        const idF =
          (sf.familia && sf.familia.id) ||
          (sf.familiaInfo && sf.familiaInfo.id) ||
          sf.idFamilia;
        return idF === idFamilia;
      });
    },
    nombreSucursalAsignacion() {
      return this.membresia.nombreSucursalAsignacion || '';
    },
    idSucursalAsignacion() {
      return this.membresia.sucursalAsignacion || '';
    },
    erroresTurno() {
      const errores = {};
      if (!this.form.turno) return errores;
      this.dias.forEach(d => {
        if (this.form.turno[`${d.key}Autorizado`]) {
          const inicio = this.form.turno[`${d.key}HoraInicio`];
          const termino = this.form.turno[`${d.key}HoraTermino`];
          if (!inicio || !termino) {
            errores[d.key] = 'Debe completar ambas horas';
          } else if (this.toSeconds(termino) <= this.toSeconds(inicio)) {
            errores[d.key] = 'Hora término debe ser mayor a hora inicio';
          }
        }
      });
      return errores;
    },
    turnoTieneErrores() {
      return Object.keys(this.erroresTurno).length > 0;
    },
    turnoModificado() {
      if (!this.form.turno || !this.turnoOriginal) return false;
      return JSON.stringify(this.form.turno) !== JSON.stringify(this.turnoOriginal);
    }
  },
  methods: {
    buildForm(m) {
      const ia = m.informacionAdicional || {};
      return {
        idFamilia: m.idFamilia,
        nombreFamilia: m.nombreFamilia || '',
        idSubFamilia: m.idSubFamilia,
        nombreSubFamilia: m.nombreSubFamilia || '',
        idPlan: m.idPlan,
        descripcionPlan: m.descripcionPlan || '',
        valorPlan: m.valorPlan != null ? m.valorPlan : 0,
        tipoIVA: m.tipoIVA || 'EXENTO',
        convenio: glosaOrString(m.convenio),
        indicadorMatricula: !!m.indicadorMatricula,
        valorMatricula: m.valorMatricula != null ? m.valorMatricula : 0,
        fechaPagoMatricula: m.fechaPagoMatricula || '',
        indicadorFactura: !!m.indicadorFactura,
        diasPermitidoCongelar: m.diasPermitidoCongelar || 0,
        duracionPlan: m.duracionPlan || 0,
        unidadDuracionPlan: glosaOrString(m.unidadDuracionPlan),
        porcentajeDescuento: m.porcentajeDescuento || 0,
        indicadorMesCalendario: !!m.indicadorMesCalendario,
        diasMesNoCalendario: m.diasMesNoCalendario || 0,
        indicadorUsoSesiones: !!m.indicadorUsoSesiones,
        sesionesLimite: m.sesionesLimite || 0,
        sesionesUtilizadas: m.sesionesUtilizadas || 0,
        indicadorPermiteRenovar: !!m.indicadorPermiteRenovar,
        expiracionRenovacion: m.expiracionRenovacion || 0,
        unidadExpiracionRenovacion: glosaOrString(m.unidadExpiracionRenovacion),
        fechaAsignacion: m.fechaAsignacion || '',
        fechaInicio: m.fechaInicio || '',
        fechaVencimiento: m.fechaVencimiento || '',
        fechaVigencia: m.fechaVigencia || '',
        fechaUltimoPago: m.fechaUltimoPago || '',
        horaUltimoPago: m.horaUltimoPago || '',
        estadoPlan: glosaOrString(m.estadoPago) || 'PENDIENTE',
        indicadorActualizarTurno: false,
        turno: m.turno ? { ...m.turno } : null,
        informacionAdicional: {
          multiSede: !!ia.multiSede,
          descuentoSesionesXDia: ia.descuentoSesionesXDia !== false,
          esSuscripcion: !!ia.esSuscripcion,
          limitarPuntoAcceso: !!ia.limitarPuntoAcceso,
          puntoAccesoPermitidos: Array.isArray(ia.puntoAccesoPermitidos)
            ? ia.puntoAccesoPermitidos.map(p => ({
                idAcceso: p.idAcceso || '',
                descontarSesion: !!p.descontarSesion
              }))
            : [],
          beneficiosAdicionales: Array.isArray(ia.beneficiosAdicionales)
            ? [...ia.beneficiosAdicionales]
            : []
        }
      };
    },
    toSeconds(hhmmss) {
      if (!hhmmss || typeof hhmmss !== 'string') return null;
      const parts = hhmmss.split(':').map(p => parseInt(p, 10));
      if (parts.some(isNaN)) return null;
      const [h = 0, m = 0, s = 0] = parts;
      return h * 3600 + m * 60 + s;
    },
    onFamiliaChange() {
      const familia = this.familiasActivas.find(f => f.id === this.form.idFamilia);
      this.form.nombreFamilia = familia ? familia.nombre : '';
      const subfamiliaActual = this.subfamiliasFiltradas.find(sf => sf.id === this.form.idSubFamilia);
      if (!subfamiliaActual) {
        const primera = this.subfamiliasFiltradas[0];
        this.form.idSubFamilia = primera ? primera.id : null;
        this.form.nombreSubFamilia = primera ? primera.nombre : '';
      }
    },
    onSubFamiliaChange() {
      const subfamilia = (this.catalogos.subfamilias || []).find(sf => sf.id === this.form.idSubFamilia);
      this.form.nombreSubFamilia = subfamilia ? subfamilia.nombre : '';
    },
    normalizarHora(hora) {
      if (!hora) return '';
      if (typeof hora !== 'string') return hora;
      const partes = hora.split(':');
      if (partes.length === 2) return `${hora}:00`;
      return hora;
    },
    normalizarTurno(turno) {
      if (!turno) return turno;
      const out = { ...turno };
      this.dias.forEach(d => {
        const ki = `${d.key}HoraInicio`;
        const kt = `${d.key}HoraTermino`;
        if (out[ki]) out[ki] = this.normalizarHora(out[ki]);
        if (out[kt]) out[kt] = this.normalizarHora(out[kt]);
      });
      return out;
    },
    agregarBeneficio() {
      this.form.informacionAdicional.beneficiosAdicionales.push('');
    },
    quitarBeneficio(idx) {
      this.form.informacionAdicional.beneficiosAdicionales.splice(idx, 1);
    },
    agregarPuntoAcceso() {
      this.form.informacionAdicional.puntoAccesoPermitidos.push({
        idAcceso: '',
        descontarSesion: false
      });
    },
    quitarPuntoAcceso(idx) {
      this.form.informacionAdicional.puntoAccesoPermitidos.splice(idx, 1);
    },
    buildParameters() {
      const tipoPlan = this.tipo === 'principal' ? 'PRINCIPAL' : 'SECUNDARIO';
      const params = {
        idSucursal: this.idSucursalAsignacion,
        nombreSucursal: this.nombreSucursalAsignacion,
        rut: this.socio.rut,
        tipoPlan,
        modalidadPlan: 'ON-LINE'
      };
      if (tipoPlan === 'SECUNDARIO') {
        params.idMembresia = this.membresia._id;
      }
      return params;
    },
    buildBody() {
      const f = this.form;
      const ia = f.informacionAdicional;
      const beneficios = (ia.beneficiosAdicionales || [])
        .map(b => (b || '').trim())
        .filter(b => b.length > 0);
      return {
        idFamilia: f.idFamilia,
        nombreFamilia: f.nombreFamilia,
        idSubFamilia: f.idSubFamilia,
        nombreSubFamilia: f.nombreSubFamilia,
        idPlan: f.idPlan,
        descripcionPlan: f.descripcionPlan,
        valorPlan: Number(f.valorPlan) || 0,
        tipoIVA: f.tipoIVA,
        convenio: f.convenio,
        indicadorMatricula: !!f.indicadorMatricula,
        valorMatricula: Number(f.valorMatricula) || 0,
        fechaPagoMatricula: f.fechaPagoMatricula || '',
        indicadorFactura: !!f.indicadorFactura,
        diasPermitidoCongelar: Number(f.diasPermitidoCongelar) || 0,
        duracionPlan: Number(f.duracionPlan) || 0,
        unidadDuracionPlan: f.unidadDuracionPlan,
        porcentajeDescuento: Number(f.porcentajeDescuento) || 0,
        indicadorMesCalendario: !!f.indicadorMesCalendario,
        diasMesNoCalendario: Number(f.diasMesNoCalendario) || 0,
        indicadorUsoSesiones: !!f.indicadorUsoSesiones,
        sesionesLimite: Number(f.sesionesLimite) || 0,
        sesionesUtilizadas: Number(f.sesionesUtilizadas) || 0,
        indicadorPermiteRenovar: !!f.indicadorPermiteRenovar,
        expiracionRenovacion: Number(f.expiracionRenovacion) || 0,
        unidadExpiracionRenovacion: f.unidadExpiracionRenovacion,
        fechaAsignacion: f.fechaAsignacion || '',
        fechaInicio: f.fechaInicio || '',
        fechaVencimiento: f.fechaVencimiento || '',
        fechaVigencia: f.fechaVigencia || '',
        fechaUltimoPago: f.fechaUltimoPago || '',
        horaUltimoPago: f.horaUltimoPago || '',
        estadoPlan: f.estadoPlan,
        indicadorActualizarTurno: this.turnoModificado,
        turno: this.normalizarTurno(f.turno) || {},
        informacionAdicional: {
          multiSede: !!ia.multiSede,
          descuentoSesionesXDia: !!ia.descuentoSesionesXDia,
          esSuscripcion: !!ia.esSuscripcion,
          limitarPuntoAcceso: !!ia.limitarPuntoAcceso,
          puntoAccesoPermitidos: ia.limitarPuntoAcceso
            ? ia.puntoAccesoPermitidos.map(p => ({
                idAcceso: p.idAcceso,
                descontarSesion: !!p.descontarSesion
              }))
            : [],
          beneficiosAdicionales: beneficios
        }
      };
    },
    async guardarCambios() {
      const params = this.buildParameters();
      if (!params.rut) {
        this.$swal.fire({
          title: 'Falta el RUT del socio',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      if (params.tipoPlan === 'SECUNDARIO' && !params.idMembresia) {
        this.$swal.fire({
          title: 'Falta el ID de la membresía',
          text: 'Para actualizar membresías secundarias se requiere el identificador único de la membresía.',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      if (this.turnoTieneErrores) {
        this.$swal.fire({
          title: 'Horarios de turno inválidos',
          text: 'Hay días con horarios donde la hora de término no es posterior a la hora de inicio. Corrige los horarios marcados antes de guardar.',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      if (!this.form.descripcionPlan || !this.form.descripcionPlan.trim()) {
        this.$swal.fire({
          title: 'La descripción del plan es obligatoria',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }

      const confirm = await this.$swal.fire({
        title: '¿Guardar cambios?',
        text: `Vas a actualizar la membresía ${params.tipoPlan} de ${this.socio.nombreSocio}.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, guardar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#6bd098',
        cancelButtonColor: '#eb6532'
      });
      if (!confirm.isConfirmed) return;

      this.saving = true;
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/membresias/socio/actualizar' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: params,
          Body: this.buildBody()
        });

        const result = response.data && response.data.result;
        if (result && result.code === 0) {
          await this.$swal.fire({
            title: '¡Membresía actualizada!',
            text: result.message || 'Los cambios se guardaron correctamente.',
            icon: 'success',
            confirmButtonColor: '#6bd098'
          });
          this.$emit('saved');
        } else {
          throw new Error((result && result.message) || 'No se pudo actualizar la membresía');
        }
      } catch (error) {
        const backendMessage =
          error.response &&
          error.response.data &&
          error.response.data.result &&
          error.response.data.result.message;
        this.$swal.fire({
          title: 'Error al actualizar la membresía',
          text: backendMessage || error.message || 'Error inesperado',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.membresia-editor section {
  scroll-margin-top: 20px;
}
.custom-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}
.custom-toggle input[type='checkbox'] {
  margin-right: 0.5rem;
  width: 1.1rem;
  height: 1.1rem;
}
code {
  font-size: 0.8rem;
  background: #f4f4f4;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
}
</style>
